import * as React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
    transformSelectedImagesToListOfIds,
    useDisableDeleteMultipleImages,
    useSelectThumbnailClickHandler
} from '../state/thumbnailUtils';
import { DigitalStylingThumbnail } from './DigitalStylingThumbnail';
import { DigitalStylingStatus, FileMetadata, ThumbnailModel } from '../types';
import { SelectAllButton } from './SelectAllButton';
import { DigitalStylingThumbnailSkeleton } from './DigitalStylingThumbnailSkeleton';
import { CustomerOrSupportImage } from '../../adminView/uneditedImages/view/UploadUneditedImages';

export type DigitalStylingThumbnailsLabels = {
    thumbnailTooltipDelete: string;
    confirmDeleteSingleTitle: string;
    confirmDeleteSingleText: string;
    confirmDeleteSingleAcceptButton: string;
    confirmDeleteSingleCancelButton: string;
    deleteMultipleButton: string;
    selectAllButton: string;
    deselectAllButton: string;
    downloadImagesButton: string;
};

type DigitalStylingThumbnailsProps = {
    files: ThumbnailModel[];
    filesInProgress: FileMetadata[];
    labels: DigitalStylingThumbnailsLabels;
    onClick?: () => void;
    onDelete: (imageId: number, isUploadedByCustomer: boolean) => void;
    onDeleteMultiple: (images: CustomerOrSupportImage[]) => void;
    onClickDownloadImages?: () => Promise<any>;
    enableActions: boolean;
};

export function DigitalStylingThumbnails({
    files,
    filesInProgress,
    labels,
    onDelete,
    onDeleteMultiple,
    enableActions,
    onClick,
    onClickDownloadImages
}: DigitalStylingThumbnailsProps) {
    let { selectedImages, handleClick, selectAll, deselectAll } = useSelectThumbnailClickHandler(
        files,
        onClick
    );
    let disableDeleteMultiple = useDisableDeleteMultipleImages(
        files,
        selectedImages,
        enableActions
    );

    function handleDeleteMultipleImages() {
        let selectedImageIds = transformSelectedImagesToListOfIds(selectedImages);

        let imagesToDelete: CustomerOrSupportImage[] = selectedImageIds.map((id) => {
            let file = files.find((file) => file.id === id);
            if (!file) {
                console.error(`File with id ${id} not found`, { files, selectedImages });
            }

            return {
                id,
                isUploadedByCustomer: file.isUploadedByCustomer
            };
        });

        for (let selectedId of selectedImageIds) {
            selectedImages.delete(selectedId);
        }

        onDeleteMultiple(imagesToDelete);
    }

    function handleSelectAll(showSelect: boolean) {
        if (showSelect) {
            selectAll();
        } else {
            deselectAll();
        }
    }

    return (
        <Box>
            <Stack sx={{ float: 'right' }} direction="row" spacing={2}>
                {typeof onClickDownloadImages === 'function' ? (
                    <Button
                        variant="contained"
                        onClick={onClickDownloadImages}
                        disabled={!enableActions}
                        startIcon={<DownloadIcon />}
                    >
                        {labels.downloadImagesButton}
                    </Button>
                ) : null}
                <SelectAllButton
                    labels={{
                        selectAllButton: labels.selectAllButton,
                        deselectAllButton: labels.deselectAllButton
                    }}
                    disabled={!enableActions}
                    onClick={handleSelectAll}
                />
                <Button
                    color="error"
                    variant="contained"
                    onClick={handleDeleteMultipleImages}
                    disabled={disableDeleteMultiple}
                    startIcon={<DeleteIcon />}
                >
                    {labels.deleteMultipleButton}
                </Button>
            </Stack>
            <Grid container sx={{ height: '100%', paddingTop: '10px' }} spacing={2}>
                {files.map(
                    ({
                        filename,
                        reference,
                        id,
                        isUploadedByCustomer,
                        highResolutionImageReference
                    }) => (
                        <Grid key={id} item xs={12} sm={6} md={4} lg={3}>
                            <DigitalStylingThumbnail
                                filename={filename}
                                isUploadedByCustomer={isUploadedByCustomer}
                                id={id}
                                imageReference={reference}
                                highResolutionImageReference={highResolutionImageReference}
                                onClick={handleClick}
                                onDelete={onDelete}
                                isSelected={selectedImages.get(id) || false}
                                labels={{
                                    tooltipDelete: labels.thumbnailTooltipDelete,
                                    confirmDeleteSingleTitle: labels.confirmDeleteSingleTitle,
                                    confirmDeleteSingleText: labels.confirmDeleteSingleText,
                                    confirmDeleteSingleCancelButton:
                                        labels.confirmDeleteSingleCancelButton,
                                    confirmDeleteSingleAcceptButton:
                                        labels.confirmDeleteSingleAcceptButton
                                }}
                            />
                        </Grid>
                    )
                )}
                {filesInProgress.map((_, index) => (
                    <DigitalStylingThumbnailSkeleton key={index} />
                ))}
            </Grid>
        </Box>
    );
}
